/**
 * N7 — o que nunca chega à tela, mesmo que o backend mande.
 *
 * A rodada 3 fotografou uma consulta de empregados cujo canvas exibia
 * `Phoneclock password: 1234` em texto puro, ao lado do nome da pessoa. O
 * catálogo publica a coluna, o mapeador copia o que chega, e a tabela imprime.
 * Nenhuma camada disse "não".
 *
 * Esta é a camada que diz. É uma rede de segurança, e vale enunciar o que ela
 * NÃO é: o valor continua trafegando na resposta até o navegador, então o
 * conserto de verdade é a fonte parar de publicá-lo. Suprimir aqui evita a
 * exposição na tela — não a exposição no fio.
 *
 * A regra decide por NOME de campo, não por valor: um valor "1234" é
 * indistinguível de um número de matrícula, mas uma coluna chamada `password`
 * declara o que carrega. Reconhece os três idiomas do produto porque o rótulo
 * pode vir traduzido, e casa por palavra inteira para não engolir campo
 * legítimo — `passwordExpiraEm` é uma data, não um segredo.
 */

/**
 * Só credencial. Documento e identificador pessoal (CPF, PIS, matrícula) ficam
 * de fora de propósito: são o assunto do produto, e escondê-los quebraria a
 * consulta que a pessoa pediu. Aqui mora o que não tem por que ser lido.
 */
const TERMOS_DE_CREDENCIAL = [
  'password',
  'passwd',
  'senha',
  'contrasena',
  'token',
  'secret',
  'segredo',
  'apikey',
  'pin',
  'hash',
  'salt',
  'credencial',
  'credential'
]

/**
 * Termos que a normalização parte em dois: `api_key` vira "api key", e nenhuma
 * das metades é credencial sozinha. Casar por substring resolveria e traria
 * junto o falso positivo ("api keyboard"), então a checagem é por par
 * ADJACENTE — precisa das duas palavras, nessa ordem, lado a lado.
 */
const PARES_DE_CREDENCIAL = [
  ['api', 'key'],
  ['api', 'secret'],
  ['chave', 'api'],
  ['chave', 'secreta'],
  ['clave', 'secreta']
]

/** `Phoneclock password` → `phoneclock password`; `senha_do_rep` → `senha do rep`. */
function normalizar (texto: string): string {
  return texto
    .normalize('NFD')
    // Intervalo escrito por código: marcas combinantes literais numa classe de
    // caractere são invisíveis no editor e somem numa cópia descuidada.
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .toLowerCase()
}

/**
 * `true` quando o campo carrega credencial. Recebe chave e rótulo porque os
 * dois chegam da wire e qualquer um dos dois pode denunciar o campo — a chave
 * costuma ser `snake_case` em inglês, o rótulo pode vir traduzido.
 */
export function campoDeCredencial (...nomes: Array<string | null | undefined>): boolean {
  for (const nome of nomes) {
    const palavras = normalizar(nome || '').split(/\s+/).filter(Boolean)
    // Palavra inteira: `passwordExpiraEm` vira ["password", "expira", "em"] e é
    // pego, mas `sword` ou `pinheiro` não viram credencial por acidente.
    if (palavras.some(palavra => TERMOS_DE_CREDENCIAL.includes(palavra))) return true

    const adjacente = PARES_DE_CREDENCIAL.some(([primeira, segunda]) =>
      palavras.some((palavra, i) => palavra === primeira && palavras[i + 1] === segunda)
    )
    if (adjacente) return true
  }
  return false
}

export interface ColunaDaTabela {
  key: string
  label: string
}

/**
 * Tira as colunas de credencial da tabela E os valores correspondentes das
 * linhas. Some a coluna inteira em vez de mascarar o valor: um `••••` anuncia
 * que existe um segredo ali e convida a procurá-lo noutro lugar, e a pessoa não
 * pediu esse campo — ela pediu a lista de empregados.
 */
export function tabelaSemCredenciais<Linha extends Record<string, unknown>> (
  colunas: ColunaDaTabela[],
  linhas: Linha[]
): { colunas: ColunaDaTabela[], linhas: Linha[], suprimidas: string[] } {
  const proibida = new Set<string>()

  for (const coluna of colunas) {
    if (campoDeCredencial(coluna.key, coluna.label)) proibida.add(coluna.key)
  }

  // As LINHAS são varridas por conta própria, e não só as colunas declaradas: a
  // wire pode trazer um campo que não consta em `colunas`, e filtrar só pelo
  // cabeçalho o deixaria passar. Cada nome é avaliado uma vez.
  const nomesNasLinhas = new Set<string>()
  for (const linha of linhas) {
    for (const nome of Object.keys(linha)) nomesNasLinhas.add(nome)
  }
  for (const nome of nomesNasLinhas) {
    if (campoDeCredencial(nome)) proibida.add(nome)
  }

  if (!proibida.size) return { colunas, linhas, suprimidas: [] }

  return {
    colunas: colunas.filter(coluna => !proibida.has(coluna.key)),
    linhas: linhas.map(linha => Object.fromEntries(
      Object.entries(linha).filter(([nome]) => !proibida.has(nome))
    ) as Linha),
    suprimidas: [...proibida]
  }
}
